import React, { useContext, useEffect, useState } from 'react';
import TenantContext from '@/contexts/TenantContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar as CalendarIcon, Clock, Search, Plus, User, FileText, Trash2, CalendarCheck, CalendarX, Activity, Hash, Tag, AlignLeft, CheckCircle2, X, Edit2, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CitasProPage = () => {
  const { tenantSlug } = useContext(TenantContext);
  const [citas, setCitas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingRecordId, setEditingRecordId] = useState(null);
  const [clientes, setClientes] = useState([]);
  
  // Form State
  const [formData, setFormData] = useState({
    folio: '',
    cliente: '',
    fecha: '',
    hora: '',
    servicio: '',
    notas: '',
    estado: 'Programada'
  });

  useEffect(() => {
    fetchCitas();
    fetchClientes();
  }, [tenantSlug]);

  const fetchClientes = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/clientes`);
      setClientes(res.data || []);
    } catch (err) {
      console.error("Error fetching clients for appointments:", err);
    }
  };

  const fetchCitas = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/citas`);
      setCitas(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error('Garantice que haya corrido el Activador de Kit 5x5 o creado el silo "citas"');
      setCitas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAdd = () => {
    setIsEditing(false);
    setEditingRecordId(null);
    setFormData({
      folio: `CIT-${Math.floor(1000 + Math.random() * 9000)}`,
      cliente: '',
      fecha: '',
      hora: '',
      servicio: '',
      notas: '',
      estado: 'Programada'
    });
    setIsSidebarOpen(true);
  };

  const handleEdit = (cita) => {
    setIsEditing(true);
    setEditingRecordId(cita._id);
    
    // Split date and time if possible
    let f = '';
    let h = '';
    if (cita.fecha_hora) {
        try {
            const dt = new Date(cita.fecha_hora);
            f = dt.toISOString().split('T')[0];
            h = dt.toTimeString().split(' ')[0].substring(0, 5);
        } catch(e) {}
    }

    setFormData({
      folio: cita.folio || cita.sku || 'N/A',
      cliente: cita.cliente || '',
      fecha: f,
      hora: h,
      servicio: cita.servicio || cita.motivo || '',
      notas: cita.notas || '',
      estado: cita.estado || 'Programada'
    });
    setIsSidebarOpen(true);
  };

  const handleSave = async () => {
    if (!formData.cliente || !formData.fecha || !formData.hora) {
      toast.error("El Cliente, la Fecha y la Hora son obligatorios");
      return;
    }
    
    try {
      // Reconstruct fecha_hora
      const combinedDateTime = `${formData.fecha}T${formData.hora}:00`;
      
      const payload = {
        data: {
          folio: formData.folio,
          cliente: formData.cliente,
          fecha_hora: combinedDateTime,
          servicio: formData.servicio,
          notas: formData.notas,
          estado: formData.estado,
          fecha_registro: isEditing ? undefined : new Date().toISOString()
        }
      };

      if (isEditing) {
        await axios.put(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/citas/${editingRecordId}`, payload.data);
        toast.success("Cita actualizada correctamente");
      } else {
        await axios.post(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/citas/inject`, payload);
        toast.success("Cita agendada en el ecosistema");
      }

      setIsSidebarOpen(false);
      fetchCitas();
    } catch (err) {
      toast.error("Error al procesar la cita");
    }
  };

  const handleDelete = async (recordId) => {
    if(!window.confirm("¿Seguro que deseas eliminar el registro de esta cita?")) return;
    try {
      await axios.delete(`${API_URL}/api/tenants/${tenantSlug}/engine/silos/citas/${recordId}`);
      toast.success("Cita eliminada");
      fetchCitas();
    } catch (err) {
      toast.error("Error al eliminar cita");
    }
  };

  const filteredCitas = citas.filter(c => 
    c.cliente?.toLowerCase().includes(searchTerm.toLowerCase()) || 
    c.servicio?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.motivo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.folio?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const programadas = citas.filter(c => (c.estado || '').toLowerCase() === 'programada').length;
  const completadas = citas.filter(c => (c.estado || '').toLowerCase() === 'completada').length;

  const formatDate = (isoString) => {
      if(!isoString) return "Fecha Pendiente";
      try {
          const d = new Date(isoString);
          return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' });
      } catch(e) { return "Fecha Pendiente"; }
  };

  const formatTime = (isoString) => {
    if(!isoString) return "--:--";
    try {
        const d = new Date(isoString);
        return d.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });
    } catch(e) { return "--:--"; }
};

  const getStatusColor = (status) => {
    switch((status || '').toUpperCase()) {
      case 'PROGRAMADA': return 'bg-rose-500/20 text-rose-400 border-rose-500/50';
      case 'COMPLETADA': return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50';
      case 'CANCELADA': return 'bg-slate-500/20 text-slate-400 border-slate-500/50';
      default: return 'bg-white/10 text-slate-300 border-white/20';
    }
  };

  return (
    <div className="h-full flex flex-col pt-8 lg:pt-0">
      {/* Premium Rose Header */}
      <div className="mb-8 relative">
        <div className="absolute inset-0 bg-rose-500/10 blur-[100px] rounded-full pointer-events-none" />
        <div className="flex flex-col gap-6 relative z-10">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
            <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <CalendarIcon className="w-4 h-4 text-rose-400" />
                <span className="text-[9px] uppercase tracking-[0.4em] font-black text-rose-400">Scheduler Engine</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-black tracking-tight text-white font-outfit leading-none">
                Fluxo <span className="text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-orange-400">Citas</span>
              </h1>
              <p className="text-slate-400 mt-3 font-medium italic text-xs max-w-xl">Gestión inteligente de agenda y disponibilidad orquestada por IA.</p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3 bg-white/[0.03] border border-white/5 p-2 rounded-[1.5rem] w-full lg:w-auto lg:min-w-[500px]"
            >
                <div className="flex items-center bg-slate-900/50 rounded-xl border border-white/5 px-2">
                  <button 
                    onClick={() => document.getElementById('citas-table-container').scrollBy({ left: -300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  <div className="w-[1px] h-4 bg-white/5 mx-1" />
                  <button 
                    onClick={() => document.getElementById('citas-table-container').scrollBy({ left: 300, behavior: 'smooth' })}
                    className="p-2 text-slate-600 hover:text-white transition-colors"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>

                <div className="relative flex-1 group">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <input
                    type="text"
                    className="block w-full pl-9 pr-4 py-2.5 bg-slate-950/50 border border-white/10 rounded-xl text-xs placeholder-slate-600 text-white focus:outline-none focus:ring-2 focus:ring-rose-500/50 transition-all font-bold tracking-tight"
                    placeholder="Buscar cita..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                  
                <button 
                  onClick={handleOpenAdd}
                  className="px-4 py-2.5 bg-gradient-to-r from-rose-600 to-orange-700 hover:from-rose-500 hover:to-orange-600 text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all shadow-lg hover:-translate-y-0.5 active:scale-95 flex items-center gap-2"
                >
                  <Plus className="w-4 h-4" />
                  <span className="hidden sm:inline">Agendar</span>
                </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 relative overflow-hidden group hover:border-rose-500/30 transition-colors">
          <div className="w-16 h-16 rounded-2xl bg-rose-500/10 flex items-center justify-center border border-rose-500/20">
            <Activity className="w-8 h-8 text-rose-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{programadas}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Sesiones Programadas</p>
          </div>
        </div>

        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 relative overflow-hidden group hover:border-emerald-500/30 transition-colors">
          <div className="w-16 h-16 rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20">
            <CalendarCheck className="w-8 h-8 text-emerald-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{completadas}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Sesiones Completadas</p>
          </div>
        </div>

        <div className="bg-slate-900 border border-white/5 rounded-[2rem] p-6 flex items-center gap-6 relative overflow-hidden group hover:border-slate-500/30 transition-colors">
          <div className="w-16 h-16 rounded-2xl bg-slate-500/10 flex items-center justify-center border border-slate-500/20">
            <FileText className="w-8 h-8 text-slate-400" />
          </div>
          <div>
             <h3 className="text-3xl font-black text-white">{citas.length}</h3>
             <p className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mt-1">Total en Agenda</p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-slate-900/50 backdrop-blur-xl border border-white/5 rounded-[2rem] flex-1 overflow-hidden flex flex-col">
          <div id="citas-table-container" className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-white/5 border-b border-white/5">
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Folio / Cliente</th>
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Servicio / Motivo</th>
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Programación</th>
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 italic">Estado</th>
                  <th className="p-6 text-[11px] font-black uppercase tracking-[0.2em] text-slate-500 text-right italic">Gestión</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {loading ? (
                   <tr>
                     <td colSpan="5" className="p-12 text-center text-slate-500 text-sm font-medium">Cronometrando agenda...</td>
                   </tr>
                ) : filteredCitas.length === 0 ? (
                   <tr>
                     <td colSpan="5" className="p-20 text-center">
                        <CalendarIcon className="w-12 h-12 text-slate-700 mx-auto mb-4" />
                        <p className="text-lg text-slate-400 font-medium">Libro de citas en blanco</p>
                     </td>
                   </tr>
                ) : (
                  filteredCitas.map((cita, index) => (
                    <motion.tr 
                      key={cita._id || index}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="hover:bg-rose-500/[0.03] transition-all group cursor-pointer border-b border-white/[0.03]"
                      onClick={() => handleEdit(cita)}
                    >
                      <td className="p-6">
                        <div className="flex items-center gap-4">
                           <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-rose-500 to-pink-600 flex items-center justify-center border border-white/10 shrink-0 text-white shadow-lg">
                              <User className="w-4 h-4" />
                           </div>
                           <div>
                             <p className="text-white font-bold text-sm tracking-wide">{cita.cliente || 'Sin Especificar'}</p>
                             <p className="text-rose-400 text-[10px] font-black uppercase tracking-widest mt-1">{cita.folio || cita.sku || 'REF-N/A'}</p>
                           </div>
                        </div>
                      </td>
                      <td className="p-6">
                         <div className="flex flex-col">
                            <span className="text-slate-300 font-medium text-sm">{cita.servicio || cita.motivo || 'Sesión Estándar'}</span>
                            {cita.notas && <span className="text-[10px] text-slate-500 italic truncate w-48">{cita.notas}</span>}
                         </div>
                      </td>
                      <td className="p-6">
                         <div className="flex flex-col">
                            <div className="flex items-center gap-2">
                                <CalendarIcon className="w-4 h-4 text-rose-500" />
                                <span className="text-slate-200 font-bold text-xs">{formatDate(cita.fecha_hora)}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                                <Clock className="w-4 h-4 text-orange-400" />
                                <span className="text-slate-400 font-bold text-[10px] uppercase tracking-wider">{formatTime(cita.fecha_hora)}</span>
                            </div>
                         </div>
                      </td>
                      <td className="p-6">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${getStatusColor(cita.estado)}`}>
                          {cita.estado || 'PROGRAMADA'}
                        </span>
                      </td>
                      <td className="p-6 text-right">
                          <div className="flex items-center justify-end gap-3">
                            <button 
                               onClick={(e) => { e.stopPropagation(); handleEdit(cita); }}
                               className="p-3 bg-rose-500/10 text-rose-400 hover:bg-rose-600 hover:text-white rounded-2xl transition-all opacity-0 group-hover:opacity-100 shadow-lg border border-rose-500/20 active:scale-95"
                               title="Editar Cita"
                             >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button 
                               onClick={(e) => { e.stopPropagation(); handleDelete(cita._id); }}
                               className="p-3 bg-slate-500/10 text-slate-400 hover:bg-rose-600 hover:text-white rounded-2xl transition-all opacity-0 group-hover:opacity-100 border border-white/5 active:scale-95"
                               title="Eliminar Registro"
                             >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                      </td>
                    </motion.tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
      </div>

      {/* Premium Sidebar Design */}
      <AnimatePresence>
        {isSidebarOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              className="fixed inset-0 bg-[#020617]/80 backdrop-blur-md z-[100]"
              onClick={() => setIsSidebarOpen(false)}
            />
            <motion.div 
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 28, stiffness: 220 }}
              className="fixed top-0 right-0 h-full w-full max-w-[500px] bg-[#0F172A] border-l border-white/10 z-[101] shadow-2xl flex flex-col overflow-hidden"
            >
              {/* Sidebar Header */}
              <div className="p-6 border-b border-white/5 bg-white/[0.02]">
                <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-rose-500/10 flex items-center justify-center border border-rose-500/20">
                            <CalendarIcon className="w-5 h-5 text-rose-400" />
                        </div>
                        <div>
                            <h2 className="text-xl font-black text-white tracking-tighter uppercase italic">{isEditing ? 'Modificar Cita' : 'Agendar Sesión'}</h2>
                            <p className="text-slate-500 text-[9px] font-black uppercase tracking-widest mt-0.5 italic">Agenda Fluxo</p>
                        </div>
                    </div>
                    <button onClick={() => setIsSidebarOpen(false)} className="p-1.5 hover:bg-white/5 rounded-lg text-slate-500 transition-colors">
                        <Plus className="w-7 h-7 rotate-45" />
                    </button>
                </div>
              </div>
              
              <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
                  {/* Identification Group */}
                  <div className="space-y-6">
                    <div className="flex items-center gap-3">
                       <div className="w-1 h-5 bg-rose-500 rounded-full" />
                       <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Identificación de Cita</span>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Folio / Referencia</label>
                        <div className="relative group">
                            <Hash className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 transition-colors group-focus-within:text-rose-500" />
                            <input 
                                type="text" 
                                readOnly
                                className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 pl-11 pr-4 text-slate-400 font-black tracking-[0.2em] outline-none cursor-not-allowed italic text-xs"
                                value={formData.folio}
                            />
                        </div>
                      </div>

                      <div className="space-y-1.5">
                        <label className="text-[9px] font-black uppercase tracking-widest text-rose-500 ml-1 italic">Participante / Cliente *</label>
                        <div className="relative group">
                            <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-rose-500/50 transition-colors group-focus-within:text-rose-500" />
                            <input 
                                type="text" 
                                list="clients-list"
                                className="w-full bg-rose-500/[0.02] border border-rose-500/20 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-rose-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                                value={formData.cliente}
                                onChange={e => setFormData({...formData, cliente: e.target.value})}
                                placeholder="Seleccionar..."
                            />
                            <datalist id="clients-list">
                                {clientes.map(c => (
                                    <option key={c._id} value={c.nombre} />
                                ))}
                            </datalist>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Schedule & Service Group */}
                  <div className="space-y-6 pt-8 border-t border-white/5">
                    <div className="flex items-center gap-3">
                       <div className="w-1 h-5 bg-orange-500 rounded-full" />
                       <span className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 italic">Horario y Servicio</span>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Fecha *</label>
                            <input 
                                type="date" 
                                className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-orange-500/50 outline-none transition-all font-black text-xs italic"
                                value={formData.fecha}
                                onChange={e => setFormData({...formData, fecha: e.target.value})}
                            />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-[9px] font-black uppercase tracking-widest text-slate-500 ml-1 italic">Hora *</label>
                            <input 
                                type="time" 
                                className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-orange-500/50 outline-none transition-all font-black text-xs italic"
                                value={formData.hora}
                                onChange={e => setFormData({...formData, hora: e.target.value})}
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Servicio / Motivo</label>
                        <div className="relative group">
                            <Tag className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600 transition-colors group-focus-within:text-orange-500" />
                            <input 
                                type="text" 
                                className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 pl-11 pr-4 text-white focus:border-orange-500/50 outline-none transition-all font-black placeholder:text-slate-800 text-xs italic"
                                value={formData.servicio}
                                onChange={e => setFormData({...formData, servicio: e.target.value})}
                                placeholder="Ej. Corte de Cabello, Consultoría..."
                            />
                        </div>
                    </div>
                  </div>

                  {/* Details & Status Group */}
                  <div className="space-y-8 pt-10 border-t border-white/5">
                    <div className="flex items-center gap-3">
                       <div className="w-1.5 h-6 bg-emerald-500 rounded-full" />
                       <span className="text-[11px] font-black uppercase tracking-[0.3em] text-slate-400 italic">Detalles & Estado</span>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Notas Adicionales</label>
                        <div className="relative group">
                            <AlignLeft className="absolute left-4 top-3 w-4 h-4 text-slate-600 transition-colors group-focus-within:text-emerald-500" />
                            <textarea 
                                rows="3"
                                className="w-full bg-black/40 border border-white/5 rounded-xl p-3 pl-11 pr-4 text-white focus:border-emerald-500/50 outline-none transition-all font-medium placeholder:text-slate-800 resize-none text-[13px] leading-relaxed italic"
                                value={formData.notas}
                                onChange={e => setFormData({...formData, notas: e.target.value})}
                                placeholder="Indicaciones especiales..."
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black uppercase tracking-widest text-slate-500 ml-1">Estado de la Cita</label>
                        <select 
                            className="w-full bg-black/40 border border-white/5 rounded-xl py-2.5 px-3 text-white focus:border-emerald-500/50 outline-none transition-all appearance-none font-black text-xs uppercase tracking-[0.2em] italic"
                            value={formData.estado}
                            onChange={e => setFormData({...formData, estado: e.target.value})}
                        >
                            <option value="Programada">Programada</option>
                            <option value="Completada">Completada</option>
                            <option value="Cancelada">Cancelada</option>
                        </select>
                    </div>
                  </div>
              </div>

              {/* Sidebar Footer */}
              <div className="p-6 border-t border-white/5 bg-white/[0.02] flex items-center gap-4">
                  <div className="flex-1 p-3 rounded-xl bg-rose-500/5 border border-rose-500/10 flex items-center gap-3">
                      <Activity className="w-5 h-5 text-rose-400" />
                      <div>
                          <p className="text-[8px] font-black text-rose-400 uppercase tracking-widest">Scheduler Live</p>
                          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-tighter">Sincronización On</p>
                      </div>
                  </div>
                  <button 
                    onClick={handleSave}
                    className="flex-[2] py-3 bg-gradient-to-r from-rose-600 to-pink-700 hover:from-rose-500 hover:to-pink-600 text-white font-black uppercase tracking-[0.2em] text-[10px] rounded-xl shadow-[0_0_20px_rgba(244,63,94,0.2)] transition-all flex items-center justify-center gap-2 active:scale-95"
                  >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>{isEditing ? 'Actualizar Agenda' : 'Confirmar en Agenda'}</span>
                  </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CitasProPage;
